/**
 * Jovii — THIS IS A TELEVISION, AND A TELEVISION IS NOT A BIG PHONE.
 *
 * Two things are wrong with the app on a Fire TV Stick and neither of them is a
 * styling opinion. Both are measured facts about how a television presents
 * itself to a WebView, and both need answering before the first paint.
 *
 * ── 1. THE PANEL EATS ITS OWN EDGES (overscan) ─────────────────────────────
 *
 * A television crops the outer few percent of every edge — a habit inherited
 * from CRTs that most sets still keep on by default. Android has required TV
 * apps to hold their interface inside a safe margin since the beginning; a web
 * page has never had to think about it, so this one did not, and the owner's
 * report is exactly the symptom: "all the borders are being cut out". The top
 * bar starts at y=0 and the last poster in every rail ends at x=1920, so on a
 * real panel the bar and the rail ends are simply not on the screen.
 *
 * The fix is a tunable inset (`TV_SAFE`) folded into the `--safe-*` tokens the
 * whole app already respects, so it costs one rule and no component knows.
 *
 * ── 2. THE PAGE IS DRAWN AT TWICE THE SIZE IT THINKS (density) ─────────────
 *
 * An Android TV WebView reports a density, and a 1080p stick reports about 2.
 * With `width=device-width` that gives a 960 CSS px layout viewport on a 1920
 * px panel: every pixel the stylesheet asks for is painted as two. A 195 px
 * poster is 390 real pixels — a fifth of the screen — and four and a half of
 * them fit across a television. That is the whole of "it's too zoomed in" and
 * most of "the rails are too large on the TV", and no amount of CSS fixes it,
 * because the CSS is being obeyed exactly.
 *
 * So the layout viewport is set explicitly instead, to a width that lands the
 * page at a known scale — see `tvViewportWidth`. It is deliberately NOT set to
 * the panel's full pixel width: 15 px body text on a 1920 px viewport is 8 mm
 * of glass, which is unreadable from a sofa. `TV_SCALE` is the number that
 * decision lives in.
 *
 * ── HOW WE KNOW IT IS A TELEVISION ─────────────────────────────────────────
 *
 * Not from a media query, and this is the trap. `focus.svelte.ts` already
 * records the measurement: an Android TV WebView reports `pointer: coarse` and
 * `hover: none` EXACTLY like a phone, which is why a Fire TV Stick used to take
 * the thumb branch of the focus layer. There is no media query that separates a
 * television from a tablet.
 *
 * So it is four independent signals, OR-ed, because each one covers a case the
 * others miss:
 *
 *   1. `AFT…` in the agent — every Fire TV model code begins AFT (AFTMM, AFTKA,
 *      AFTSSS…), and Android WebView puts the model in the agent. This is the
 *      one that works on the shell that is ALREADY INSTALLED, with no update.
 *   2. A named television platform — Android TV, Google TV, BRAVIA, Tizen,
 *      webOS, a Chromecast. Covers a browser on a smart TV, which the shell
 *      never sees.
 *   3. The shell's own `TV` token, which it appends when Android's
 *      `UiModeManager` says television. Definitive, and the only signal that
 *      survives Chrome's user-agent reduction — newer WebViews replace the
 *      device model with a bare "K" and signal 1 stops working.
 *   4. Android WITH NO TOUCHSCREEN. `maxTouchPoints` is 0 on a set-top box and
 *      never 0 on a phone or a tablet, so this catches a reduced agent on a
 *      stick that has not been updated. It is last because it is the inference
 *      rather than the statement.
 *
 * And one override on top, because a wrong answer three metres from the keyboard
 * is not a thing anybody can debug: `?tv=1` forces it on, `?tv=0` forces it off,
 * and either choice is remembered.
 */

/**
 * REAL PIXELS PER CSS PIXEL ON A TELEVISION.
 *
 * 1.5, and the arithmetic is the argument. At 1080p a pixel of panel is about
 * 0.57 mm tall, so body text at `--text-base` (15 px) is 15 × 1.5 × 0.57 ≈
 * 13 mm, which subtends about 15 arc-minutes at three metres — the size a road
 * sign is designed around, and comfortably above the ~10 arc-minutes at which
 * reading becomes work.
 *
 * The rejected values say the same thing from both sides. At 2 (what the stick
 * does today) the type is luxurious and only four and a half posters fit across
 * the screen, which is the complaint. At 1 the whole 1920 px is available and
 * nine posters fit, but body text falls to 8.6 mm / 10 arc-minutes and a poster
 * caption to 7 — a wall of unreadable small print.
 */
export const TV_SCALE = 1.5;

/**
 * THE SAFE MARGIN, as a fraction of each edge.
 *
 * Android's own television guidance is 5% horizontally and vertically, which is
 * the worst case a badly adjusted set will crop. 4% is the number here: it is
 * inside the range every panel made this decade actually crops, and the 1% back
 * is 19 px of screen returned to the picture on each side. Overshooting is not
 * free on a television — the interface is already short of room.
 */
export const TV_SAFE = 0.04;

/** The narrowest and widest layout the television branch will ask for. */
export const TV_MIN_WIDTH = 960;
export const TV_MAX_WIDTH = 1600;

/** Fire TV, every generation: the model code always begins AFT. */
const FIRE_TV = /\bAFT[A-Z0-9]{1,8}\b/;

/**
 * Named television platforms. Written out rather than matching a bare "TV",
 * which appears in far too many ordinary strings to be a signal.
 */
const TV_PLATFORM =
  /\b(?:Android\s?TV|Google\s?TV|GoogleTV|SMART[-\s]?TV|SmartTV|BRAVIA|Tizen|Web0S|WebOS|NetCast|HbbTV|CrKey|Chromecast|AppleTV|Roku|VIDAA|AQUOS|DTV)\b/i;

/** The token this app's own Android shell appends when Android says television. */
const SHELL_TV = /\bJoveShell\/[^\s]+\s+TV\b/;

export interface TvSignals {
  /** `navigator.userAgent`. */
  ua: string;
  /** `navigator.maxTouchPoints`. A set-top box has none; a phone always has some. */
  touchPoints: number;
  /** `?tv=1` / `?tv=0` / a remembered choice. `null` means nobody has said. */
  forced?: boolean | null;
}

/**
 * IS THIS A TELEVISION. Pure, so `tv.test.ts` can put a Fire TV Stick, a Pixel,
 * an iPad and a laptop through it without any of them being present.
 */
export function isTv({ ua, touchPoints, forced = null }: TvSignals): boolean {
  if (forced !== null) return forced;
  if (SHELL_TV.test(ua)) return true;
  if (FIRE_TV.test(ua)) return true;
  if (TV_PLATFORM.test(ua)) return true;
  // The inference: Android, and nothing on the device can be touched.
  return /\bAndroid\b/.test(ua) && touchPoints === 0;
}

/**
 * THE LAYOUT WIDTH A TELEVISION SHOULD BE GIVEN, from the panel's real width.
 *
 * `physicalWidth` is `screen.width × devicePixelRatio` — the actual pixels
 * across, whatever the device chose to call them. Dividing by `TV_SCALE` is the
 * whole idea: it normalises every television to the same real-world type size,
 * so a stick that reports 960 CSS px at density 2 and a set that reports the
 * full 1920 at density 1 both end up laying out at 1280 and looking identical.
 *
 * The clamp is for the ends of the range that arithmetic alone gets wrong. A
 * 4K panel would ask for 2560, which is a desktop layout on a sofa; a 720p one
 * would ask for 853, which is narrower than the phone breakpoint and would hand
 * a television the phone's tab bar.
 */
export function tvViewportWidth(physicalWidth: number): number {
  if (!Number.isFinite(physicalWidth) || physicalWidth <= 0) return TV_MIN_WIDTH;
  return Math.round(Math.min(TV_MAX_WIDTH, Math.max(TV_MIN_WIDTH, physicalWidth / TV_SCALE)));
}

/** `?tv=1` / `?tv=0`, remembered once given. Anything else leaves it to the sniff. */
function override(search: string, store: Storage | null): boolean | null {
  let asked: boolean | null = null;
  const raw = new URLSearchParams(search).get('tv');
  if (raw === '1' || raw === 'on' || raw === 'true') asked = true;
  else if (raw === '0' || raw === 'off' || raw === 'false') asked = false;

  try {
    if (asked !== null) {
      store?.setItem('jt.tv', asked ? '1' : '0');
      return asked;
    }
    const saved = store?.getItem('jt.tv');
    if (saved === '1') return true;
    if (saved === '0') return false;
  } catch {
    // A WebView with storage disabled still gets the URL answer above.
  }
  return asked;
}

/**
 * DECIDE, AND SAY SO IN THE ONE PLACE EVERYTHING ELSE READS.
 *
 * Called from `main.ts` BEFORE the app is mounted, which matters twice: the
 * `data-tv` attribute has to be on `<html>` before the first rule is matched, or
 * the television gets a frame of the desktop layout; and the viewport meta has
 * to be rewritten while `<body>` is still empty, or rewriting it reflows a
 * painted page.
 *
 * Returns whether this is a television, so a caller can log it.
 */
export function applyTv(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  let store: Storage | null = null;
  try {
    store = window.localStorage;
  } catch {
    store = null;
  }

  const tv = isTv({
    ua: navigator.userAgent,
    touchPoints: navigator.maxTouchPoints ?? 0,
    forced: override(window.location.search, store)
  });

  const root = document.documentElement;
  if (!tv) {
    delete root.dataset.tv;
    return false;
  }
  root.dataset.tv = 'on';

  /*
   * READ THE PANEL BEFORE CHANGING THE VIEWPORT, not after: once the layout
   * width is pinned, `screen.width × devicePixelRatio` returns the width we
   * just asked for and the sum becomes a loop that converges on nothing.
   */
  const physical = Math.round((window.screen?.width ?? window.innerWidth) * (window.devicePixelRatio || 1));
  const width = tvViewportWidth(physical);
  root.style.setProperty('--tv-width', `${width}px`);

  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (meta !== null) meta.setAttribute('content', `width=${width}, viewport-fit=cover`);

  return true;
}
